// ChatBox.tsx
import "katex/dist/katex.min.css";
import { useEffect, useRef } from "react";
import type { useChatSession } from "../../hooks/useChatSession";
import { useFileUpload } from "../../hooks/useFileUpload";
import type { DataResponse } from "../../types/dataResponse";
import FileUpload, { AttachedFilesList, UploadStatus } from "../FileUpload";
import "./markdown.css";
import { default as MessagesContainer, type ChatMessage } from "./Messages";

// Helper component for input area
function MessageInput({ 
  input, 
  handleInputChange, 
  handleCustomSubmit, 
  textareaRef, 
  resizeTextarea, 
  isUploading,
  isProcessing
}: {
  input: string,
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void,
  handleCustomSubmit: (e: React.FormEvent) => Promise<void>,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  resizeTextarea: () => void,
  isUploading: boolean,
  isProcessing: boolean
}) {
  const isDisabled = isUploading || isProcessing;
  
  return (
    <textarea
      ref={textareaRef}
      value={input}
      onChange={handleInputChange}
      onInput={resizeTextarea}
      onKeyDown={async (e) => {
        if (e.key === "Enter" && !e.shiftKey && !isDisabled) {
          e.preventDefault();
          await handleCustomSubmit(e);
        }
      }}
      placeholder={isProcessing ? "Processing previous message..." : "Next, let's…"}
      className={`w-full border-none outline-none p-1 text-sm bg-transparent resize-none overflow-y-auto ${
        isDisabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
      style={{ minHeight: '36px', maxHeight: '108px', lineHeight: '20px' }}
      disabled={isDisabled}
      rows={1}
    />
  );
}


export default function ChatBox({
  chatSession,
  onResponse,
  sessionId,
  userID,
  messagesHeightClass,
  preservedMessages,
  onMessagesUpdate,
  onFileUploadWithMessage,
  pendingMessage,
  onPendingMessageProcessed,
}: {
  chatSession: ReturnType<typeof useChatSession>;
  onResponse?: (data: DataResponse[]) => void;
  sessionId: string | null;
  userID: string | null;
  messagesHeightClass?: string;
  preservedMessages?: Array<ChatMessage>;
  onMessagesUpdate?: (messages: ChatMessage[]) => void;
  onFileUploadWithMessage?: (message: string) => void;
  pendingMessage?: ChatMessage | null;
  onPendingMessageProcessed?: () => void;
}) {  
  // Extract chat functionality from the session
  const {
    input, 
    handleInputChange, 
    data, 
    chatMessages,
    status,
    customStatusMessage,
    restoreMessages,
    processPendingMessage,
    handleCustomSubmit
  } = chatSession;
  
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasRun = useRef(false); // find a better way to do this, prevents two messages after start page

  // Convert AI messages to our ChatMessage format
  useEffect(() => {
    if (preservedMessages && preservedMessages.length > 0) {
      restoreMessages(preservedMessages);
    }
  }, [preservedMessages, restoreMessages]);

  // Handle pending message from anywhere (StartPage, tab switch, etc.)
  useEffect(() => {
    if (pendingMessage && pendingMessage.content.trim() && chatSession.status !== 'streaming' && !hasRun.current) {
      processPendingMessage(pendingMessage, onFileUploadWithMessage, onPendingMessageProcessed);
      onPendingMessageProcessed?.();
      hasRun.current = true;
    }
  }, [pendingMessage]); //, processPendingMessage, onFileUploadWithMessage, onPendingMessageProcessed]);

  // Update parent with current messages - use a slight delay to ensure message is processed
  useEffect(() => {
    if (onMessagesUpdate && chatMessages.length > 0) {
      // Use setTimeout to ensure the message is fully processed before updating parent
      const timeoutId = setTimeout(() => {
        onMessagesUpdate(chatMessages);
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [chatMessages, onMessagesUpdate]);

  // Use shared file upload hook
  const {
    attachedFiles,
    isUploading,
    handleFilesSelected,
    handleRemoveFile,
    uploadFiles,
    clearFiles
  } = useFileUpload({ sessionId, userID });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "auto" });
  }, [chatMessages, status, customStatusMessage, messagesHeightClass]);

  // Auto-resize textarea up to 5 lines
  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = 20; // adjusted line height
      const padding = 8; // account for padding
      const minHeight = 36; // minimum height
      const maxHeight = (lineHeight * 5) + padding; // 5 lines max
      const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
      textarea.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    console.log('Looking at new tab data:', data);
    console.log('Data type:', typeof data);
    console.log('Data is array:', Array.isArray(data));
    console.log('Status:', status);
    // Only process response data when streaming is complete (status is 'ready')
    if (data && Array.isArray(data) && status === 'ready') {
      console.log('Received data as array (streaming complete):', data);
      onResponse?.(data);
    }
  }, [data, onResponse, status]);

  // Simple form submit handler that uses chatSession's logic
  const handleFormSubmit = async (e: React.FormEvent) => {
    // Prevent submission if already processing
    if (status === 'submitted' || status === 'streaming') {
      e.preventDefault();
      return;
    }
    await handleCustomSubmit(e, input, attachedFiles, uploadFiles, clearFiles, onResponse);
  };

  // Determine if we're currently processing a message
  const isProcessing = status === 'submitted' || status === 'streaming';

    return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-hidden">
        <MessagesContainer
          messages={chatMessages}
          messagesHeightClass="h-full"
          bottomRef={bottomRef}
          isStreaming={status === 'streaming'}
          agentStatus={
            customStatusMessage ? customStatusMessage : // Show backend status messages if available
            (status === 'submitted' ? '' : null) // Show generic loading dots only during 'submitted'
          }
        />
      </div>
      <form onSubmit={handleFormSubmit} className="px-4 pb-4 pt-3 space-y-3">
        <div className="flex items-center gap-2 w-full">
          <FileUpload 
            onFilesSelected={handleFilesSelected}
            disabled={isProcessing}
          />
          <div className="flex-1 min-w-0">
            <div className={`flex flex-col w-full border rounded-lg bg-white px-2 py-1 ${
              isProcessing ? 'opacity-60 pointer-events-none' : ''
            }`}>            
              <AttachedFilesList 
                attachedFiles={attachedFiles} 
                onRemoveFile={handleRemoveFile} 
              />
              
              <MessageInput
                input={input} 
                handleInputChange={handleInputChange} 
                handleCustomSubmit={handleFormSubmit} 
                textareaRef={textareaRef} 
                resizeTextarea={resizeTextarea} 
                isUploading={isUploading}
                isProcessing={isProcessing}
              />
            </div>
          </div>
        </div>
        <UploadStatus isUploading={isUploading} />
      </form>
    </div>
  );
}
